import React, { useState } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, Badge, Card, CardBody, Button, Input, Alert, Spinner } from 'reactstrap';
import { motion } from 'framer-motion';
import { formatCreatedDate, formatRoleName, toBackendDateTime, apiDateTimeToInputValue, nowLocalInputValue } from '../../constants/utils';
import { useTranslation } from 'react-i18next';
import api from '../../helpers/api';
import usePermission from '../../Hooks/usePermission';

/**
 * Detail modal for an asset-transfer request/log entry. Unlike the generic EquipmentModal
 * (acquisition-shaped: manufacturer, vendor, quantity, price), this renders the fields that
 * actually describe a transfer: the asset(s), from → to location, permanent/temporary type,
 * start/end dates, reason, and who requested/actioned it.
 *
 * Footer actions (all request-level, POSTing to equipment/asset-transfer/*):
 *  - Edit (return date + reason): admins on any editable transfer; the requester on their own
 *    still-PENDING request. The backend reschedules an ongoing temporary transfer's auto-return.
 *  - Approve / Reject: admins on PENDING requests, so a decision doesn't require closing the
 *    details and finding the row again.
 * Each action calls {@code onUpdated(updatedTransfer)} so the parent can refresh its list and
 * swap the open modal's data in place.
 */
const statusColor = (status) => ({
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  PERMANENT_TRANSFERRED: 'info',
  TEMPORARY_TRANSFERRED: 'primary',
}[status] || 'secondary');

const TransferDetailsModal = ({ isOpen, toggle, transfer, onUpdated }) => {
  const { t } = useTranslation();
  const { isAdmin, user } = usePermission();
  const [activeTab, setActiveTab] = useState('info');
  const [actionError, setActionError] = useState(null);
  const [deciding, setDeciding] = useState(null); // 'approve' | 'reject' | null
  const [rejectDialog, setRejectDialog] = useState({ open: false, reason: '' });
  const [editDialog, setEditDialog] = useState({ open: false, endDate: '', reason: '', saving: false, error: null });

  if (!transfer) return null;

  const rows = transfer.processInfo || [];
  const first = rows[0] || {};
  const isTemporary = !!first.endDate;
  const reason = first.reason;

  const statusLabel = (s) => t(`shared.${(s || '').toLowerCase()}`, (s || 'N/A').replace(/_/g, ' '));

  // Synthetic fallback rows (asset surfaces without a resolved parent request) have no id — no actions.
  const isOwner = !!user?.id && user.id === transfer.requestedBy?.id;
  const canEdit = !!transfer.id && transfer.status !== 'REJECTED'
    && (isAdmin || (isOwner && transfer.status === 'PENDING'));

  // Read defensively: this runs during render, and malformed storage would take the whole modal
  // down rather than degrade one badge.
  let activeOrganizationId = null;
  try {
    activeOrganizationId = JSON.parse(localStorage.getItem('authUser') || '{}')?.activeOrganizationId ?? null;
  } catch {
    activeOrganizationId = null;
  }
  const isIncoming = transfer.crossOrg
    && String(transfer.destinationOrganization?.id) === String(activeOrganizationId);

  // Only the side the request is waiting on may decide it: a cross-org transfer at the DESTINATION
  // stage belongs to the receiving organization's admins, never to the sender's.
  const canDecide = isAdmin && !!transfer.id && transfer.status === 'PENDING'
    && (!transfer.crossOrg
      || (transfer.approvalStage === 'DESTINATION'
        ? isIncoming
        : String(transfer.sourceOrganization?.id) === String(activeOrganizationId)));

  // The offering side can retract while it waits, without needing the other side to act.
  const canWithdraw = !!transfer.id && transfer.status === 'PENDING'
    && String(transfer.sourceOrganization?.id ?? activeOrganizationId) === String(activeOrganizationId)
    && (isAdmin || isOwner);

  const stageLabel = transfer.crossOrg && transfer.status === 'PENDING'
    ? (transfer.approvalStage === 'DESTINATION'
      ? t('business.planning.awaiting_destination_approval', 'Awaiting destination approval')
      : t('business.planning.awaiting_source_approval', 'Awaiting source approval'))
    : null;

  const withdraw = async () => {
    setDeciding('withdraw');
    setActionError(null);
    try {
      const { data } = await api.post('equipment/asset-transfer/withdraw', { equipmentId: transfer.id });
      // Let the navigation drop the Planning badge right away instead of on its next poll.
      window.dispatchEvent(new Event('transfers-updated'));
      if (onUpdated) onUpdated(data?.response);
    } catch (e) {
      setActionError(
        e?.response?.data?.message ||
          t('business.planning.update_failed', 'Failed to update the transfer. Please try again.')
      );
    } finally {
      setDeciding(null);
    }
  };

  const decide = async (approve, rejectReason) => {
    setDeciding(approve ? 'approve' : 'reject');
    setActionError(null);
    try {
      const { data } = await api.post('equipment/asset-transfer/decision', {
        equipmentId: transfer.id,
        approve,
        rejectReason: rejectReason || null,
      });
      window.dispatchEvent(new Event('transfers-updated'));
      if (onUpdated) onUpdated(data?.response);
    } catch (e) {
      // Unlike the list rows, surface the failure (e.g. someone else already decided this request).
      setActionError(
        e?.response?.data?.message ||
          t('business.planning.update_failed', 'Failed to update the transfer. Please try again.')
      );
    } finally {
      setDeciding(null);
    }
  };

  const submitReject = async () => {
    const rejectReason = rejectDialog.reason;
    setRejectDialog({ open: false, reason: '' });
    await decide(false, rejectReason);
  };

  const openEdit = () => setEditDialog({
    open: true,
    endDate: isTemporary ? apiDateTimeToInputValue(first.endDate) : '',
    reason: reason || '',
    saving: false,
    error: null,
  });

  // datetime-local values share one lexicographically ordered format, so string compares work.
  const startLocal = first.startDate ? apiDateTimeToInputValue(first.startDate) : '';
  const minEndLocal = startLocal > nowLocalInputValue() ? startLocal : nowLocalInputValue();
  const editEndValid = !isTemporary
    || (!!editDialog.endDate && editDialog.endDate > nowLocalInputValue() && (!startLocal || editDialog.endDate > startLocal));

  const submitEdit = async () => {
    if (!editEndValid || editDialog.saving) return;
    setEditDialog((prev) => ({ ...prev, saving: true, error: null }));
    try {
      const { data } = await api.post('equipment/asset-transfer/update', {
        equipmentId: transfer.id,
        endDate: isTemporary ? toBackendDateTime(editDialog.endDate) : null,
        reason: editDialog.reason?.trim() || null,
      });
      setEditDialog({ open: false, endDate: '', reason: '', saving: false, error: null });
      if (onUpdated) onUpdated(data?.response);
    } catch (e) {
      setEditDialog((prev) => ({
        ...prev,
        saving: false,
        error: e?.response?.data?.message ||
          t('business.planning.update_failed', 'Failed to update the transfer. Please try again.'),
      }));
    }
  };

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle}>
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Header */}
        <div
          className="modal-header border-0 text-white position-sticky"
          style={{ top: 0, zIndex: 10, background: 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)', padding: '1.5rem' }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-20 rounded-circle d-flex align-items-center justify-content-center me-3"
                 style={{ width: '40px', height: '40px' }}>
              <i className="bx bx-transfer-alt text-white fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title mb-0 fw-bold text-white">{transfer.requestId || t('business.planning.transfer', 'Transfer')}</h5>
              <p className="text-white-50 small mb-0">{t('business.planning.transfer_details_subtitle', 'Asset transfer details')}</p>
            </div>
          </div>
          <button onClick={toggle} type="button" className="btn-close btn-close-white" aria-label={t('shared.close', 'Close')} />
        </div>

        {/* Tabs (single Info tab, mirrors EquipmentModal) */}
        <Nav tabs className="border-bottom-0 px-4 pt-3 bg-light" style={{ borderRadius: '0' }}>
          <NavItem>
            <NavLink
              className={`nav-link-modern ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
              style={{
                border: 'none', borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'info' ? '#fff' : 'transparent',
                color: activeTab === 'info' ? '#495057' : '#6c757d',
                fontWeight: activeTab === 'info' ? '600' : '400', cursor: 'pointer',
              }}
            >
              <i className="bx bx-info-circle me-2"></i>{t('shared.info', 'Info')}
            </NavLink>
          </NavItem>
        </Nav>

        <div className="modal-body p-0" style={{ maxHeight: 'calc(90vh - 200px)', overflow: 'auto' }}>
          <TabContent activeTab={activeTab}>
            <TabPane tabId="info">
              <div className="p-4">
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-4">
                    <Row className="g-4">
                      {/* Transfer overview */}
                      <Col md={6}>
                        <div className="info-card bg-light rounded p-3 h-100">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-transfer-alt me-2"></i>{t('business.planning.transfer_overview', 'Transfer Overview')}
                          </h6>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.status', 'Status')}</label>
                            <div>
                              <Badge color={statusColor(transfer.status)} className="px-3 py-2"
                                     style={{ borderRadius: '50px', fontSize: '11px', fontWeight: '500' }}>
                                {statusLabel(transfer.status)}
                              </Badge>
                            </div>
                          </div>
                          {transfer.crossOrg && (
                            <div className="info-item mb-3">
                              <label className="text-muted small">{t('shared.organization', 'Organization')}</label>
                              <div className="fw-medium">
                                {transfer.sourceOrganization?.name || 'N/A'}
                                <i className="bx bx-right-arrow-alt mx-1"></i>
                                {transfer.destinationOrganization?.name || 'N/A'}
                              </div>
                            </div>
                          )}
                          {stageLabel && (
                            <div className="info-item mb-3">
                              <label className="text-muted small">{t('business.planning.approval', 'Approval')}</label>
                              <div className="fw-medium">{stageLabel}</div>
                            </div>
                          )}
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.transfer_type', 'Transfer Type')}</label>
                            <div className="fw-medium">
                              {isTemporary
                                ? t('business.planning.temporary_transfer', 'Temporary Transfer')
                                : t('business.planning.permanent_transfer', 'Permanent Transfer')}
                            </div>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.start_date', 'Start Date')}</label>
                            <div className="fw-medium">{first.startDate ? formatCreatedDate(first.startDate) : 'N/A'}</div>
                          </div>
                          {isTemporary && (
                            <div className="info-item">
                              <label className="text-muted small">{t('shared.return_date', 'Return Date')}</label>
                              <div className="fw-medium">{formatCreatedDate(first.endDate)}</div>
                            </div>
                          )}
                        </div>
                      </Col>

                      {/* Request / action information */}
                      <Col md={6}>
                        <div className="info-card bg-light rounded p-3 h-100">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-user me-2"></i>{t('shared.request_information', 'Request Information')}
                          </h6>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('business.planning.requested_by', 'Requested By')}</label>
                            <div className="fw-medium">{transfer.requestedBy?.name || 'N/A'}</div>
                            <small className="text-muted">{formatRoleName(transfer.requestedBy?.role, t) || ''}</small>
                          </div>
                          <div className="info-item mb-3">
                            <label className="text-muted small">{t('shared.request_date', 'Request Date')}</label>
                            <div className="fw-medium">{transfer.requestDate ? formatCreatedDate(transfer.requestDate) : 'N/A'}</div>
                          </div>
                          {transfer.actionTakenBy && (
                            <>
                              <div className="info-item mb-3">
                                <label className="text-muted small">{t('business.planning.actioned_by', 'Actioned By')}</label>
                                <div className="fw-medium">{transfer.actionTakenBy.name || 'N/A'}</div>
                                <small className="text-muted">{formatRoleName(transfer.actionTakenBy.role, t) || ''}</small>
                              </div>
                              <div className="info-item">
                                <label className="text-muted small">{t('shared.action_date', 'Action Date')}</label>
                                <div className="fw-medium">{transfer.actionDate ? formatCreatedDate(transfer.actionDate) : 'N/A'}</div>
                              </div>
                            </>
                          )}
                        </div>
                      </Col>

                      {/* Assets & locations (handles bulk transfers) */}
                      <Col md={12}>
                        <div className="info-card bg-light rounded p-3">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-package me-2"></i>{t('business.planning.assets_and_locations', 'Assets & Locations')}
                          </h6>
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th>{t('shared.asset', 'Asset')}</th>
                                  <th>{t('shared.from', 'From')}</th>
                                  <th>{t('shared.to', 'To')}</th>
                                </tr>
                              </thead>
                              <tbody>
                                {rows.map((p, i) => (
                                  <tr key={p.id || i}>
                                    <td className="fw-medium">{p.linkedAsset?.name || 'N/A'}</td>
                                    <td>{p.fromLocationsInfo?.name || 'N/A'}</td>
                                    <td>{p.locationsInfo?.name || 'N/A'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </Col>

                      {/* Reason */}
                      <Col md={12}>
                        <div className="info-card bg-light rounded p-3">
                          <h6 className="text-primary mb-3 fw-semibold">
                            <i className="bx bx-file-text me-2"></i>{t('shared.reason', 'Reason')}
                          </h6>
                          <div className="fw-medium">{reason || t('shared.n_a', 'N/A')}</div>
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </div>
            </TabPane>
          </TabContent>
        </div>

        {/* Close sits alone on the left; record actions group on the right so the footer breathes. */}
        <div className="modal-footer border-0 pt-0 justify-content-between">
          {actionError && <Alert color="danger" className="w-100 py-2 mb-2">{actionError}</Alert>}
          <button type="button" className="btn btn-secondary" onClick={toggle}>{t('shared.close', 'Close')}</button>
          <div className="d-flex gap-2">
            {canEdit && (
              <Button color="primary" outline onClick={openEdit}>{t('shared.edit', 'Edit')}</Button>
            )}
            {canWithdraw && (
              <Button color="secondary" outline disabled={!!deciding} onClick={withdraw}>
                {deciding === 'withdraw' && <Spinner size="sm" className="me-2" />}
                {t('business.planning.withdraw', 'Withdraw')}
              </Button>
            )}
            {canDecide && (
              <>
                <Button
                  color="danger"
                  outline
                  disabled={!!deciding}
                  onClick={() => setRejectDialog({ open: true, reason: '' })}
                >
                  {deciding === 'reject' && <Spinner size="sm" className="me-2" />}
                  {t('business.planning.reject', 'Reject')}
                </Button>
                <Button color="success" disabled={!!deciding} onClick={() => decide(true)}>
                  {deciding === 'approve' && <Spinner size="sm" className="me-2" />}
                  {t('business.planning.approve', 'Approve')}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Reject-reason prompt (mirrors the TransfersTab row action) */}
        <Modal isOpen={rejectDialog.open} toggle={() => setRejectDialog({ open: false, reason: '' })} centered>
          <ModalHeader toggle={() => setRejectDialog({ open: false, reason: '' })}>
            {t('business.planning.reject_transfer', 'Reject Transfer Request')}
          </ModalHeader>
          <ModalBody>
            <label className="form-label">{t('business.planning.reject_reason', 'Reason (optional)')}</label>
            <Input
              type="textarea"
              rows={3}
              value={rejectDialog.reason}
              onChange={(e) => setRejectDialog((prev) => ({ ...prev, reason: e.target.value }))}
              placeholder={t('business.planning.reject_reason_placeholder', 'Why is this transfer being rejected?')}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setRejectDialog({ open: false, reason: '' })}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="danger" onClick={submitReject}>
              {t('business.planning.reject', 'Reject')}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Edit return date / reason */}
        <Modal isOpen={editDialog.open} toggle={() => setEditDialog((prev) => ({ ...prev, open: false }))} centered>
          <ModalHeader toggle={() => setEditDialog((prev) => ({ ...prev, open: false }))}>
            {t('business.planning.edit_transfer', 'Edit Transfer')}
          </ModalHeader>
          <ModalBody>
            {editDialog.error && <Alert color="danger">{editDialog.error}</Alert>}
            {isTemporary && (
              <div className="mb-3">
                <label className="form-label">{t('shared.return_date', 'Return Date')} *</label>
                <Input
                  type="datetime-local"
                  min={minEndLocal}
                  value={editDialog.endDate}
                  onChange={(e) => setEditDialog((prev) => ({ ...prev, endDate: e.target.value }))}
                />
                {!editEndValid && (
                  <span className="text-danger small">
                    {t('business.planning.return_date_future_error', 'The return date must be in the future and after the start date.')}
                  </span>
                )}
                <span className="text-muted small d-block">
                  {t('business.planning.auto_return_note', 'The asset returns to its previous location automatically at the end date.')}
                </span>
              </div>
            )}
            <div className="mb-3">
              <label className="form-label">{t('shared.reason', 'Reason')}</label>
              <Input
                type="textarea"
                rows={2}
                value={editDialog.reason}
                onChange={(e) => setEditDialog((prev) => ({ ...prev, reason: e.target.value }))}
                placeholder={t('business.planning.transfer_reason_placeholder', 'Reason for the transfer (optional)')}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setEditDialog((prev) => ({ ...prev, open: false }))} disabled={editDialog.saving}>
              {t('shared.cancel', 'Cancel')}
            </Button>
            <Button color="primary" onClick={submitEdit} disabled={!editEndValid || editDialog.saving}>
              {editDialog.saving && <Spinner size="sm" className="me-2" />}
              {editDialog.saving ? t('shared.saving', 'Saving...') : t('shared.save', 'Save')}
            </Button>
          </ModalFooter>
        </Modal>
      </motion.div>
    </Modal>
  );
};

export default TransferDetailsModal;
